import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AffiliateShell from "@/components/feature/AffiliateShell";
import { FitImage } from "@/components/feature/EventMedia";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { deltaPercent, type Kpi } from "@/services/affiliate";
import {
  cancelReservation,
  endTime,
  EventFullError,
  fetchEvents,
  fetchEventsDashboard,
  formatDateLong,
  formatDuration,
  formatTimeRange,
  isLive,
  isUpcoming,
  KIND_META,
  reserveEvent,
  trackEvent,
  type CalEvent,
  type EventKind,
  type EventsDashboard,
  type Pair,
} from "@/services/events";

const PURPLE = "#7C3AED";
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const BAR_COLORS = ["#10B981", "#3B82F6", "#7C3AED", "#F59E0B", "#14B8A6", "#EC4899", "#64748B", "#F97316"];

type Tab = "proximas" | "pasadas" | "grabaciones" | "canceladas";
const TABS: { key: Tab; label: string }[] = [
  { key: "proximas", label: "Próximas" },
  { key: "pasadas", label: "Pasadas" },
  { key: "grabaciones", label: "Grabaciones" },
  { key: "canceladas", label: "Canceladas" },
];

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>{children}</section>;
}

function Delta({ p, suffix }: { p: Pair; suffix: string }) {
  const d = deltaPercent({ total: 0, cur: p.cur, prev: p.prev } as Kpi);
  if (d === null) return <span className="text-[11px] text-slate-400">Sin datos previos</span>;
  const up = d >= 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      <i className={up ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i> {up ? "+" : ""}
      {d}% {suffix}
    </span>
  );
}

function hoursLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function Donut({ segments, total }: { segments: { value: number; color: string }[]; total: number }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg viewBox="0 0 140 140" className="w-36 h-36 flex-shrink-0" role="img" aria-label={`Total de participaciones: ${total}`}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="#EEF2F7" strokeWidth="16" />
      {total > 0
        ? segments.map((s, i) => {
            const len = (s.value / total) * C;
            const el = (
              <circle key={i} cx="70" cy="70" r={R} fill="none" stroke={s.color} strokeWidth="16" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} transform="rotate(-90 70 70)" />
            );
            acc += len;
            return el;
          })
        : null}
      <text x="70" y="68" textAnchor="middle" fontSize="26" fontWeight="800" fill="#0B1F3A">
        {total}
      </text>
      <text x="70" y="86" textAnchor="middle" fontSize="9" fill="#64748B">
        Participaciones
      </text>
    </svg>
  );
}

function MiniCalendar({ events, selected, onSelect }: { events: CalEvent[]; selected: string; onSelect: (k: string) => void }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    events
      .filter((e) => !e.cancelled)
      .forEach((e) => {
        const k = dayKey(new Date(e.startsAt));
        m.set(k, [...(m.get(k) ?? []), e]);
      });
    return m;
  }, [events]);
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [...Array(offset).fill(null), ...Array.from({ length: dim }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))];
  while (cells.length % 7 !== 0) cells.push(null);
  const label = cursor.toLocaleDateString("es", { month: "long", year: "numeric" });
  const now = Date.now();

  return (
    <div>
      <div className="flex items-center justify-between">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} aria-label="Mes anterior" className="w-7 h-7 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <div className="text-sm font-bold text-slate-900 capitalize">{label}</div>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Mes siguiente" className="w-7 h-7 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 text-center text-[10px] font-semibold text-slate-400">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-y-1 text-center">
        {cells.map((d, i) => {
          if (!d) return <div key={i}></div>;
          const k = dayKey(d);
          const evs = byDay.get(k) ?? [];
          const isToday = k === dayKey(today);
          const isSel = k === selected;
          const live = evs.some((e) => isLive(e, now));
          const hasMine = evs.some((e) => e.reserved);
          return (
            <button
              key={i}
              onClick={() => onSelect(k)}
              aria-label={`${d.getDate()}${evs.length ? `, ${evs.length} reunión(es)` : ""}`}
              className={`mx-auto w-8 h-9 rounded-full flex flex-col items-center justify-center text-xs font-semibold cursor-pointer ${
                isSel ? "bg-[#7C3AED] text-white" : live ? "bg-[#7C3AED]/15 text-[#7C3AED]" : isToday ? "ring-1 ring-[#7C3AED] text-[#7C3AED]" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {d.getDate()}
              <span className="h-1.5 flex items-center">
                {evs.length ? <span className="w-1.5 h-1.5 rounded-full" style={{ background: isSel ? "#fff" : hasMine ? "#10B981" : "#A78BFA" }}></span> : null}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#A78BFA]"></span>Programada
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#10B981]"></span>Reservada por ti
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#7C3AED]/30"></span>En vivo
        </span>
      </div>
    </div>
  );
}

export default function ReunionesPanel() {
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [dash, setDash] = useState<EventsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState<Tab>("proximas");
  const [selectedDay, setSelectedDay] = useState(dayKey(new Date()));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const load = useCallback(async () => {
    try {
      const [ev, d] = await Promise.all([fetchEvents(), fetchEventsDashboard()]);
      setEvents(ev);
      setDash(d);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    load().finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const toggleReserve = async (e: CalEvent) => {
    if (e.reserved && !window.confirm("¿Cancelar tu reserva para esta reunión?")) return;
    setBusyId(e.id);
    try {
      if (e.reserved) {
        await cancelReservation(e.id);
        showToast("Reserva cancelada");
      } else {
        await reserveEvent(e.id);
        showToast("¡Cupo reservado!");
      }
    } catch (err) {
      showToast(err instanceof EventFullError ? "Esta reunión ya no tiene cupos." : "No se pudo completar. Intenta de nuevo.");
    } finally {
      setBusyId(null);
      load();
    }
  };

  const upcoming = useMemo(() => events.filter((e) => isUpcoming(e)), [events]);
  const recordings = useMemo(() => events.filter((e) => e.recordingUrl).sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()), [events]);
  const dayEvents = useMemo(() => events.filter((e) => !e.cancelled && dayKey(new Date(e.startsAt)) === selectedDay), [events, selectedDay]);

  const mine = useMemo(() => {
    const m = events.filter((e) => e.reserved);
    const now = Date.now();
    return {
      proximas: m.filter((e) => isUpcoming(e, now)),
      pasadas: m.filter((e) => !e.cancelled && endTime(e).getTime() <= now).sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
      grabaciones: m.filter((e) => e.recordingUrl),
      canceladas: m.filter((e) => e.cancelled),
    } as Record<Tab, CalEvent[]>;
  }, [events]);

  if (loading) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-500">
          <i className="ri-loader-4-line animate-spin text-3xl"></i>
          <p className="mt-2 text-sm">Cargando tus reuniones…</p>
        </div>
      </AffiliateShell>
    );
  }

  if (error || !dash) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-600">
          <i className="ri-error-warning-line text-3xl text-rose-500"></i>
          <p className="mt-2 text-sm">No pudimos cargar el panel de reuniones.</p>
          <button onClick={load} className="mt-3 px-4 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-semibold cursor-pointer">
            Reintentar
          </button>
        </div>
      </AffiliateShell>
    );
  }

  const kpis = [
    { label: "Reuniones programadas", sub: "Esta semana", icon: "ri-calendar-event-line", tint: "bg-violet-50 text-violet-600", value: String(dash.week.cur), p: dash.week, suffix: "vs. semana anterior" },
    { label: "Reuniones completadas", sub: "Este mes", icon: "ri-checkbox-circle-line", tint: "bg-emerald-50 text-emerald-600", value: String(dash.completed.cur), p: dash.completed, suffix: "vs. mes anterior" },
    { label: "Asistencias totales", sub: "Este mes", icon: "ri-group-line", tint: "bg-amber-50 text-amber-500", value: String(dash.attendance.cur), p: dash.attendance, suffix: "vs. mes anterior" },
    { label: "Horas reunidas", sub: "Este mes", icon: "ri-time-line", tint: "bg-sky-50 text-sky-600", value: hoursLabel(dash.minutes.cur), p: dash.minutes, suffix: "vs. mes anterior" },
  ];

  const stats = [
    { l: "En vivo", v: dash.stats.live, c: PURPLE },
    { l: "Programadas", v: dash.stats.scheduled, c: "#3B82F6" },
    { l: "Grabaciones vistas", v: dash.stats.recordings, c: "#F59E0B" },
  ];
  const statsTotal = stats.reduce((s, x) => s + x.v, 0);

  const statusOf = (e: CalEvent): { label: string; cls: string } => {
    if (e.cancelled) return { label: "Cancelada", cls: "bg-rose-50 text-rose-600" };
    if (isLive(e)) return { label: "En vivo", cls: "bg-emerald-50 text-emerald-700" };
    if (endTime(e).getTime() <= Date.now()) return { label: "Completada", cls: "bg-slate-100 text-slate-600" };
    return { label: "Programada", cls: "bg-violet-50 text-violet-700" };
  };

  return (
    <AffiliateShell>
      <div className="max-w-[1180px] mx-auto flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A] flex items-center gap-2">
              <i className="ri-calendar-event-fill text-[#7C3AED]"></i> Reuniones
            </h1>
            <p className="text-sm text-slate-500 mt-1">Organiza, participa y saca el máximo provecho de cada reunión.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/app/reuniones" className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl px-3.5 py-2.5 text-xs font-semibold hover:border-violet-300 cursor-pointer whitespace-nowrap">
              <i className="ri-layout-grid-line"></i> Vista simple
            </Link>
            {dash.isAdmin ? (
              <Link to="/app/admin/eventos" className="inline-flex items-center gap-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl px-4 py-2.5 text-xs font-semibold cursor-pointer whitespace-nowrap">
                <i className="ri-add-line"></i> Nueva reunión
              </Link>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((k) => (
            <Card key={k.label} className="p-4">
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${k.tint}`}>
                  <i className={k.icon}></i>
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-slate-600 leading-tight">{k.label}</div>
                  <div className="font-heading font-extrabold text-2xl text-[#0B1F3A] leading-tight">{k.value}</div>
                  <div className="text-[10px] text-slate-400">{k.sub}</div>
                </div>
              </div>
              <div className="mt-1.5">
                <Delta p={k.p} suffix={k.suffix} />
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8 flex flex-col gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-slate-900">Próximas reuniones</h2>
                <Link to="/app/reuniones" className="text-xs font-semibold text-[#7C3AED] cursor-pointer">
                  Ver calendario <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
              {upcoming.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No hay reuniones programadas por ahora.</p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100">
                  {upcoming.slice(0, 5).map((e) => {
                    const d = new Date(e.startsAt);
                    const live = isLive(e);
                    const soon = !live && d.getTime() - Date.now() <= 15 * 60000 && d.getTime() > Date.now();
                    const full = e.capacity !== null && e.registered >= e.capacity && !e.reserved;
                    const canJoin = (live || soon) && !!e.joinUrl;
                    return (
                      <li key={e.id} className="py-3 flex items-center gap-3">
                        <div className="w-12 flex-shrink-0 rounded-xl bg-violet-50 text-center py-1.5">
                          <div className="text-[10px] font-bold text-[#7C3AED] uppercase">{d.toLocaleDateString("es", { month: "short" }).replace(".", "")}</div>
                          <div className="font-heading font-extrabold text-lg text-[#0B1F3A] leading-none">{d.getDate()}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-bold text-slate-900 truncate">{e.title}</div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <span>
                              <i className="ri-time-line"></i> {formatTimeRange(e)}
                            </span>
                            {live ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">En vivo</span>
                            ) : e.reserved ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">Reservado</span>
                            ) : full ? (
                              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold">Cupo lleno</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-semibold">Registro abierto</span>
                            )}
                          </div>
                        </div>
                        <span className="hidden sm:inline text-[11px] text-slate-500 whitespace-nowrap">
                          <i className="ri-group-line"></i> {e.registered}
                          {e.capacity ? `/${e.capacity}` : ""}
                        </span>
                        {canJoin ? (
                          <a
                            href={e.joinUrl as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent(e.id, "join")}
                            className="px-4 py-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold cursor-pointer whitespace-nowrap"
                          >
                            Unirme
                          </a>
                        ) : (
                          <button
                            onClick={() => toggleReserve(e)}
                            disabled={busyId === e.id || full}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                              e.reserved ? "border border-violet-200 text-[#7C3AED] hover:bg-violet-50" : "bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                            }`}
                          >
                            {e.reserved ? "Cancelar reserva" : "Reservar"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {upcoming.length > 5 ? (
                <Link to="/app/reuniones" className="mt-2 block text-center text-xs font-semibold text-[#7C3AED] cursor-pointer">
                  Ver todas las reuniones <i className="ri-arrow-right-line"></i>
                </Link>
              ) : null}
            </Card>

            <Card className="p-5">
              <h2 className="font-heading font-bold text-slate-900">Mis reuniones</h2>
              <div className="mt-3 flex gap-1 border-b border-slate-200 overflow-x-auto" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px cursor-pointer ${tab === t.key ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  >
                    {t.label}
                    {mine[t.key].length ? <span className="ml-1.5 text-[10px] text-slate-400">{mine[t.key].length}</span> : null}
                  </button>
                ))}
              </div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="text-[11px] text-slate-500">
                      <th className="text-left font-semibold py-2">Reunión</th>
                      <th className="text-left font-semibold py-2">Fecha</th>
                      <th className="text-center font-semibold py-2">Asistentes</th>
                      <th className="text-left font-semibold py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mine[tab].length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-sm text-slate-500">
                          {tab === "proximas" ? "Aún no has reservado ninguna reunión." : tab === "pasadas" ? "Todavía no tienes reuniones pasadas." : tab === "grabaciones" ? "Tus reuniones con grabación aparecerán aquí." : "No tienes reuniones canceladas."}
                        </td>
                      </tr>
                    ) : (
                      mine[tab].slice(0, 6).map((e) => {
                        const st = statusOf(e);
                        return (
                          <tr key={e.id} className="border-t border-slate-100">
                            <td className="py-2.5 pr-2 font-semibold text-slate-900 text-[13px]">
                              {tab === "grabaciones" && e.recordingUrl ? (
                                <a href={e.recordingUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(e.id, "recording")} className="hover:text-[#7C3AED] cursor-pointer">
                                  <i className="ri-play-circle-line mr-1.5 text-[#7C3AED]"></i>
                                  {e.title}
                                </a>
                              ) : (
                                e.title
                              )}
                            </td>
                            <td className="py-2.5 text-xs text-slate-600 whitespace-nowrap">
                              {new Date(e.startsAt).toLocaleString("es", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                            </td>
                            <td className="py-2.5 text-center text-xs font-semibold text-slate-700">{e.registered}</td>
                            <td className="py-2.5">
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${st.cls}`}>{st.label}</span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-5">
                <h2 className="font-heading font-bold text-slate-900">Mis estadísticas</h2>
                <div className="mt-3 flex items-center gap-4">
                  <Donut segments={stats.map((s) => ({ value: s.v, color: s.c }))} total={statsTotal} />
                  <ul className="flex flex-col gap-2 text-xs">
                    {stats.map((s) => (
                      <li key={s.l} className="flex items-center gap-2 text-slate-600">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.c }}></span>
                        {s.l}
                        <span className="font-bold text-slate-900 ml-1">{s.v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>

              <Card className="p-5">
                <h2 className="font-heading font-bold text-slate-900">Participación de mi equipo</h2>
                <p className="text-[11px] text-slate-500">Reuniones a las que asistió cada persona (últimos 90 días)</p>
                {dash.team.length === 0 ? (
                  <p className="mt-4 text-sm text-slate-500">Cuando tengas personas en tu equipo directo, verás aquí su participación.</p>
                ) : (
                  <ul className="mt-3 flex flex-col gap-2.5">
                    {dash.team.slice(0, 5).map((m, i) => (
                      <li key={m.id} className="flex items-center gap-2.5">
                        <MemberAvatar name={m.fullName ?? "?"} src={m.avatarUrl} size="sm" />
                        <span className="w-24 text-xs font-semibold text-slate-800 truncate">{m.fullName ?? "Sin nombre"}</span>
                        <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <span className="block h-full rounded-full" style={{ width: `${m.percent}%`, background: BAR_COLORS[i % BAR_COLORS.length] }}></span>
                        </span>
                        <span className="w-9 text-right text-[11px] font-bold text-slate-700">{m.percent}%</span>
                        <span className="w-10 text-right text-[10px] text-slate-400">
                          {m.attended}/{m.eligible}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-4">
            <Card className="p-5">
              <h2 className="font-heading font-bold text-slate-900 mb-3">Calendario</h2>
              <MiniCalendar events={events} selected={selectedDay} onSelect={setSelectedDay} />
              <div className="mt-3 border-t border-slate-100 pt-3">
                {dayEvents.length === 0 ? (
                  <p className="text-xs text-slate-500">No hay reuniones este día.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {dayEvents.map((e) => (
                      <li key={e.id} className="rounded-lg bg-slate-50 px-3 py-2">
                        <div className="text-xs font-bold text-slate-900">{e.title}</div>
                        <div className="text-[11px] text-slate-500">{formatTimeRange(e)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-bold text-slate-900">Grabaciones recientes</h2>
                <Link to="/app/reuniones" className="text-xs font-semibold text-[#7C3AED] cursor-pointer">
                  Ver todas
                </Link>
              </div>
              {recordings.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">Cuando una reunión tenga grabación, la verás aquí.</p>
              ) : (
                <ul className="mt-3 flex flex-col gap-3">
                  {recordings.slice(0, 4).map((e) => (
                    <li key={e.id}>
                      <a href={e.recordingUrl as string} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent(e.id, "recording")} className="flex items-center gap-3 cursor-pointer group">
                        <span className={`relative w-20 h-12 rounded-lg overflow-hidden bg-gradient-to-br ${KIND_META[e.kind as EventKind].gradient} flex items-center justify-center flex-shrink-0`}>
                          <FitImage event={e} />
                          <i className="ri-play-fill relative text-white text-xl"></i>
                          {e.recordingSeconds ? <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[9px] px-1 rounded">{formatDuration(e.recordingSeconds)}</span> : null}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-xs font-bold text-slate-900 truncate group-hover:text-[#7C3AED]">{e.title}</span>
                          <span className="block text-[11px] text-slate-500 truncate">{e.hostName ? `Con ${e.hostName}` : formatDateLong(e.startsAt)}</span>
                          <span className="block text-[10px] text-slate-400">{new Date(e.startsAt).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" })}</span>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>

        {dash.isAdmin ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 flex flex-col md:flex-row md:items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-2xl flex-shrink-0">
              <i className="ri-vidicon-line"></i>
            </span>
            <div className="flex-1">
              <div className="font-heading font-bold text-slate-900 text-sm">Crea reuniones impactantes</div>
              <p className="text-xs text-slate-600 mt-0.5">Programa masterclass, orientaciones y entrenamientos. Tu equipo reserva su cupo y recibe recordatorios por WhatsApp.</p>
            </div>
            <Link to="/app/admin/eventos" className="px-5 py-2.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
              Crear reunión ahora
            </Link>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="p-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-violet-50 text-[#7C3AED] flex items-center justify-center text-xl flex-shrink-0">
              <i className="ri-calendar-check-line"></i>
            </span>
            <div className="flex-1">
              <div className="text-xs font-bold text-slate-900">Integración con tu calendario</div>
              <p className="text-[11px] text-slate-500">Agrega cada reunión reservada a Google Calendar, Apple u Outlook desde la vista simple.</p>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-emerald-50 text-[#20A464] flex items-center justify-center text-xl flex-shrink-0">
              <i className="ri-whatsapp-line"></i>
            </span>
            <div className="flex-1">
              <div className="text-xs font-bold text-slate-900">Recordatorios por WhatsApp</div>
              <p className="text-[11px] text-slate-500">Activa los avisos para no perderte ninguna reunión.</p>
            </div>
            <Link to="/app/notificaciones" className="text-xs font-semibold text-[#7C3AED] cursor-pointer whitespace-nowrap">
              Configurar
            </Link>
          </Card>
        </div>
      </div>

      {toast ? <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AffiliateShell>
  );
}
